/**
 * MetadataCodeEditor — Container component bridging the @ncoder/code-editor
 * package with shell-web's Zustand store, hooks, and API layer.
 *
 * This is the only file that knows about both the package and the app's internals.
 * It maps store state → package props and package callbacks → store actions.
 */

import { useMemo, useCallback, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "next-themes";
import { toast } from "react-hot-toast";
import { Loader2, GitMerge, AlertCircle } from "lucide-react";
import type { FileNode } from "@ncoder/ui";
import { BundleNavStrip } from "@/components/molecules/metadata_v1/BundleNavStrip";
import {
  TabbedCodeEditor,
  type EditorTab as PackageEditorTab,
  type FormatApi,
} from "@ncoder/code-editor";
import { getMonacoLanguage, getFormatApiLanguage } from "@ncoder/ui";
import {
  useMetadataEditorStore,
  type EditorTab as StoreEditorTab,
} from "@/stores/metadataEditorStore";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { useActivityStore } from "@/stores/activityStore";
import { useFormatCode } from "@/hooks/metadata_v1";
import { useGitFile, useGitSelectedRepo, useDefaultGitBranch } from "@/hooks/git";
import { useEditorDeployment } from "./useEditorDeployment";
import { useEditorSave } from "./useEditorSave";
import { useEditorBranch } from "./useEditorBranch";
import { RulesBar } from "./RulesBar";
import { canEditRules, rulesScopeOf } from "@/services/rules/ids";
import type { ResolvedConflictInput } from "@/services/metadata_v1/types";

// Maps Salesforce metadata type → Salesforce DX directory name
const SFDX_DIR: Record<string, string> = {
  ApexClass:              "classes",
  ApexTrigger:            "triggers",
  LightningComponentBundle: "lwc",
  AuraDefinitionBundle:   "aura",
  Flow:                   "flows",
  CustomObject:           "objects",
  ApexPage:               "pages",
  ApexComponent:          "components",
  StaticResource:         "staticresources",
  PermissionSet:          "permissionsets",
  Profile:                "profiles",
  Layout:                 "layouts",
  CustomTab:              "tabs",
};

function buildGithubPath(metadataType: string | undefined, fileName: string): string {
  const dir = SFDX_DIR[metadataType ?? ""] ?? metadataType?.toLowerCase() ?? "unknown";
  return `force-app/main/default/${dir}/${fileName}`;
}


// ── Props ──

export interface MetadataCodeEditorProps {
  onOpenAIPanel?: () => void;
  isAIPanelOpen?: boolean;
  /** Called after a file is saved successfully — e.g. to trigger post-save analysis */
  onFileSaved?: (data: { id: string; fileName: string; metadataType: string }) => void;
  /** Called when the user selects a file (e.g. from the bundle nav strip). */
  onFileSelect?: (file: FileNode) => void;
}

// ── Component ──

export function MetadataCodeEditor({
  onOpenAIPanel,
  isAIPanelOpen = false,
  onFileSaved,
  onFileSelect,
}: MetadataCodeEditorProps) {
  const { theme } = useTheme();

  // ── Store state ──
  const tabs = useMetadataEditorStore((state) => state.tabs);
  const activeTabId = useMetadataEditorStore((state) => state.activeTabId);
  const setActiveTab = useMetadataEditorStore((state) => state.setActiveTab);
  const closeTab = useMetadataEditorStore((state) => state.closeTab);
  const reorderTabs = useMetadataEditorStore((state) => state.reorderTabs);
  const updateContent = useMetadataEditorStore((state) => state.updateContent);
  const promotePreviewTab = useMetadataEditorStore(
    (state) => state.promotePreviewTab,
  );

  // Active tab from store
  const activeTab = tabs.find((t) => t.id === activeTabId);

  // ── Auth (for deployment) ──
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const orgId = selectedOrg?.id || "";

  // Rules tabs: no branch/deploy/merge/GitHub chrome; ORG_RULES.md is read-only for non-admins.
  const rulesScope = activeTab ? rulesScopeOf(activeTab.file.id) : null;
  const isRulesTab = rulesScope !== null;
  const rulesReadOnly = rulesScope !== null && !canEditRules(rulesScope, selectedOrg?.role);

  const hasAnyActivityRunning = useActivityStore((state) => state.isAnyActivityRunning());

  // ── Hooks ──
  const { isDeploying, deploymentMessage, editedTabsCount, handleDeploy } =
    useEditorDeployment({ orgId, tabs });

  const { isSaving, isSyncing, handleSave, handleSyncFile } = useEditorSave({
    useStore: true,
    activeTab,
    activeTabId,
    registerShortcuts: false,
    onFileSaved,
  });

  const {
    handleToggleViewMain,
    isMerging,
    handleAbandon,
    isAbandoning,
    conflict,
    handleResolveConflict,
    handleDismissConflict,
  } = useEditorBranch(activeTab);

  // ── GitHub view ──
  const setViewingGithub = useMetadataEditorStore((s) => s.setViewingGithub);
  const setGithubContentForTab = useMetadataEditorStore((s) => s.setGithubContentForTab);
  const markSaved = useMetadataEditorStore((s) => s.markSaved);

  // Subscribe — when data arrives after viewingGithub=true, re-render fires useGitFile
  const { data: selectedGitRepo, isLoading: isLoadingRepo, status: repoStatus } = useGitSelectedRepo({ enabled: !!orgId });
  // User's configured branch from GET /default_branch — not the repo's GitHub default (always "main")
  const { data: defaultGitBranch } = useDefaultGitBranch();
  const gitRef = defaultGitBranch?.name ?? selectedGitRepo?.default_branch;

  const githubPath = activeTab
    ? buildGithubPath(activeTab.file.metadataType, activeTab.file.name)
    : null;

  // Fetch file from the selected repo + user's configured branch
  const { data: githubFile, isLoading: isLoadingGithub, isFetched: isGithubFetched } = useGitFile(
    activeTab?.viewingGithub && selectedGitRepo ? selectedGitRepo.full_name : null,
    activeTab?.viewingGithub ? githubPath : null,
    gitRef,
  );

  // No repo selected in the git module
  const githubNoRepo =
    activeTab?.viewingGithub && repoStatus === 'success' && selectedGitRepo == null;

  // File not found: repo selected but file doesn't exist at expected path
  const githubNotFound =
    !githubNoRepo && activeTab?.viewingGithub && isGithubFetched && !isLoadingGithub && githubFile == null;

  const hasBranch = !!activeTab?.activeBranch;

  // Cache fetched GitHub content into the store tab
  const prevGithubPath = activeTab?.viewingGithub ? githubPath : null;
  if (activeTab && githubFile && prevGithubPath && activeTab.githubContent !== githubFile.content) {
    setGithubContentForTab(activeTab.id, githubFile.content);
  }

  // Before a branch exists, viewing GitHub means the user wants to start
  // editing FROM the GitHub content — adopt it as the working buffer once it
  // loads. Guarded on !isDirty so it never clobbers in-progress edits.
  if (
    activeTab &&
    activeTab.viewingGithub &&
    !hasBranch &&
    githubFile &&
    !activeTab.isDirty &&
    activeTab.content !== githubFile.content
  ) {
    markSaved(activeTab.id, githubFile.content, undefined);
  }

  const handleToggleViewGithub = useCallback(() => {
    if (!activeTabId) return;
    const tab = tabs.find((t) => t.id === activeTabId);
    if (!tab) return;
    if (tab.viewingGithub) {
      setViewingGithub(activeTabId, false);
    } else {
      if (tab.viewingMain) handleToggleViewMain();
      setViewingGithub(activeTabId, true);
    }
  }, [activeTabId, tabs, handleToggleViewMain, setViewingGithub]);

  // ── Format API adapter ──
  const { mutateAsync: formatCodeAsync } = useFormatCode();

  const formatApi: FormatApi = useMemo(
    () => ({
      format: async (code: string, _language: string): Promise<string> => {
        const formatLang = getFormatApiLanguage(
          activeTab?.file.metadataType,
          activeTab?.file.name,
        );

        if (!formatLang) {
          toast.error("Formatting not supported for this file type");
          throw new Error("Unsupported format language");
        }

        const result = await formatCodeAsync({ code, language: formatLang });

        if (result.error) {
          toast.error(`Format warning: ${result.error}`);
        }

        if (result.formatted !== code) {
          toast.success("Code formatted");
        } else {
          toast.success("Code already formatted");
        }

        return result.formatted;
      },
    }),
    [activeTab, formatCodeAsync],
  );

  // ── Map store tabs → package tabs ──
  const packageTabs: PackageEditorTab[] = useMemo(
    () => tabs.map(mapStoreTab),
    [tabs],
  );

  // ── Bundle nav strip (sub-header between tabs and editor) ──
  const bundleSiblings = activeTab?.bundleSiblings;
  const subHeader: ReactNode =
    activeTab && rulesScope ? (
      <RulesBar
        tabId={activeTab.id}
        scope={rulesScope}
        orgId={orgId}
        readOnly={rulesReadOnly}
        isDirty={activeTab.isDirty}
      />
    ) : activeTab && bundleSiblings && bundleSiblings.length > 0 && onFileSelect ? (
      <BundleNavStrip
        activeFile={activeTab.file}
        siblings={bundleSiblings}
        onSelectFile={onFileSelect}
      />
    ) : undefined;

  // ── Theme ──
  const editorTheme = (theme === "dark" ? "vs-dark" : "vs") as
    | "vs"
    | "vs-dark";

  // ── Tab handlers ──
  const handleTabChange = useCallback(
    (tabId: string) => setActiveTab(tabId),
    [setActiveTab],
  );

  const handleTabClose = useCallback(
    (tabId: string) => closeTab(tabId),
    [closeTab],
  );

  /** Drag (or Alt+Arrow) proposed an order; the store decides and re-renders us. */
  const handleTabReorder = useCallback(
    (orderedTabIds: string[]) => reorderTabs(orderedTabIds),
    [reorderTabs],
  );

  const handleTabDoubleClick = useCallback(
    (tabId: string) => {
      const tab = tabs.find((t) => t.id === tabId);
      if (tab?.isPreview) {
        promotePreviewTab(tabId);
      }
    },
    [tabs, promotePreviewTab],
  );

  const handleContentChange = useCallback(
    (tabId: string, value: string) => updateContent(tabId, value),
    [updateContent],
  );

  // ── Render ──
  return (
    <div className="flex flex-col h-full min-h-0 relative">
      <TabbedCodeEditor
        tabs={packageTabs}
        activeTabId={activeTabId}
        onTabChange={handleTabChange}
        onTabClose={handleTabClose}
        onTabDoubleClick={handleTabDoubleClick}
        onTabReorder={handleTabReorder}
        onContentChange={handleContentChange}
        theme={editorTheme}
        formatApi={formatApi}
        onSave={handleSave}
        onSync={isRulesTab ? undefined : handleSyncFile}
        onOpenAIPanel={isRulesTab ? undefined : onOpenAIPanel}
        readOnly={rulesReadOnly}
        subHeader={subHeader}
        onDeploy={hasBranch || isRulesTab ? undefined : handleDeploy}
        onMerge={undefined}
        onAbandon={hasBranch && !isRulesTab ? handleAbandon : undefined}
        onToggleViewMain={hasBranch && !isRulesTab ? handleToggleViewMain : undefined}
        onToggleViewGithub={isRulesTab ? undefined : handleToggleViewGithub}
        isLoadingContent={activeTab?.isLoading ?? false}
        isDirty={activeTab?.isDirty ?? false}
        isSaving={isSaving}
        isSyncing={isSyncing}
        isEdited={activeTab?.isEdited ?? false}
        editedFilesCount={editedTabsCount}
        isDeploying={isDeploying}
        deploymentMessage={deploymentMessage || undefined}
        isAIPanelOpen={isAIPanelOpen}
        hasAnyActivityRunning={hasAnyActivityRunning}
        branchName={activeTab?.activeBranch?.name}
        viewingMain={activeTab?.viewingMain ?? false}
        isMerging={isMerging}
        isAbandoning={isAbandoning}
        viewingGithub={activeTab?.viewingGithub ?? false}
        githubContent={githubNotFound ? null : (activeTab?.githubContent ?? null)}
        isLoadingGithub={isLoadingGithub}
      />

      {/* GitHub no-repo overlay — user hasn't selected a repo in the git module */}
      {githubNoRepo && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-background/95 backdrop-blur-sm gap-3">
          <div className="flex flex-col items-center gap-2 text-center max-w-sm">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">No GitHub repository selected</p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Go to the <strong className="text-foreground">Git module</strong> and select a repository first, then come back to view this file on GitHub.
            </p>
            <div className="flex items-center gap-2 mt-1">
              <Link
                to="/studio/git"
                className="px-3 py-1.5 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Go to Git module
              </Link>
              <button
                type="button"
                onClick={handleToggleViewGithub}
                className="px-3 py-1.5 text-xs rounded-md bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
              >
                Back to Local
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GitHub not-found overlay */}
      {githubNotFound && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-background/95 backdrop-blur-sm gap-3">
          <div className="flex flex-col items-center gap-3 text-center max-w-xs">
            <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Oops, file not found</p>
              <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                <span className="font-mono text-foreground">{activeTab?.file.name}</span> isn't in{" "}
                <span className="font-mono text-foreground">{selectedGitRepo?.full_name}</span>{" "}
                on <span className="font-mono text-foreground">{gitRef}</span>.
                Try switching repo or branch, or push this file to GitHub first.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleToggleViewGithub}
                className="px-3 py-1.5 text-xs rounded-md bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
              >
                Back to Local
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inline conflict resolution panel — rendered below the editor */}
      {conflict && (
        <div className="border-t border-yellow-500/30 bg-yellow-500/5 px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs">
          <div className="flex items-center gap-2 text-yellow-400 font-medium shrink-0">
            <GitMerge className="h-3.5 w-3.5" />
            Merge conflict
          </div>
          <span className="min-w-0 text-muted-foreground">
            Main was updated since this branch was created. How do you want to resolve?
          </span>
          <div className="flex items-center gap-2 ml-auto shrink-0">
            <button
              onClick={() => handleResolveConflict({ resolution: "USE_BRANCH" } as ResolvedConflictInput)}
              className="px-2.5 py-1 rounded-md bg-blue-500/15 text-blue-400 border border-blue-500/25 hover:bg-blue-500/25 transition-colors font-medium"
            >
              Keep mine
            </button>
            <button
              onClick={() => handleResolveConflict({ resolution: "USE_MAIN" } as ResolvedConflictInput)}
              className="px-2.5 py-1 rounded-md bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors font-medium"
            >
              Use main
            </button>
            <button
              onClick={handleDismissConflict}
              className="px-2 py-1 rounded-md text-muted-foreground hover:text-foreground transition-colors"
            >
              {isMerging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Cancel"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers ──

/**
 * Map a store EditorTab (domain-specific) to the package EditorTab (generic).
 */
function mapStoreTab(storeTab: StoreEditorTab): PackageEditorTab {
  return {
    id: storeTab.id,
    label: storeTab.file.name,
    fileName: storeTab.file.name,
    language: getMonacoLanguage(
      storeTab.file.metadataType,
      storeTab.file.name,
    ),
    // When viewing main, show cached main content (read-only preview)
    value: storeTab.viewingMain ? storeTab.mainContent : storeTab.content,
    isDirty: (storeTab.viewingMain || storeTab.viewingGithub) ? false : storeTab.isDirty,
    isPreview: storeTab.isPreview,
    isPinned: storeTab.isPinned,
    meta: {
      fileId: storeTab.file.id,
      metadataType: storeTab.file.metadataType,
    },
  };
}
